/* src/modules/tutors/api.js */

import gql from 'graphql-tag';
import { TUTOR_LESSON_FRAGMENT } from '../lessons/api';

const DATE_FRAGMENT = gql`
  fragment DateFragment on Date {
    timestamp
  }
`;

const STATISTICS_FRAGMENT = gql`
  fragment StatisticsFragment on Statistics {
    minCount
    count
  }
`;

const TUTOR_USER_FRAGMENT = gql`
  fragment TutorUserFragment on User {
    _id
    first
    last
    phone
    email
    avatar
    education {
      university
      year
      major
      minor
      type
      honors
    }
    howDidYouHear
  }
`;

const TUTOR_COURSE_APPROVAL_FRAGMENT = gql`
  fragment TutorCourseApprovalFragment on CourseApproval {
    course {
      _id
      subject
      number
      title
      university {
        _id
        name
      }
    }
    status
    price
    active
  }
`;

const TUTOR_SCHEDULE_FRAGMENT = gql`
  fragment TutorScheduleFragment on Schedule {
    _id
    pattern {
      repeat
      shape
    }
    startDate {
      ...DateFragment
    }
    timeblocks {
      _id
      startTime {
        ...DateFragment
      }
      endTime {
        ...DateFragment
      }
    }
  }
  ${DATE_FRAGMENT}
`;

export const REVIEW_FORM_FRAGMENT = gql`
  fragment ReviewFormFragment on ReviewForm {
    _id
    blurb
    fields {
      _id
      type
      name
      description
      required
    }
  }
`;

export const REVIEW_CUSTOM_FRAGMENT = gql`
  fragment ReviewCustomFragment on ReviewCustomized {
    _id
    anonymous
    visible
    fields {
      _id
      value
    }
    createdAt {
      timestamp
    }
    form {
      ...ReviewFormFragment
    }
    to {
      _id
      first
      last
    }
    from {
      _id
      first
      last
    }
  }
  ${REVIEW_FORM_FRAGMENT}
`;

export const APPLICANT_FRAGMENT = gql`
  fragment ApplicantFragment on Applicant {
    _id
    status
    reason
    qAndA {
      referenceID
      question
      answer
    }
    ratings {
      knowledge
      helpfulness
      organization
    }
  }
`;

export const FETCH_TUTORS_QUERY = gql`
  query fetchTutors($tutorSearchInput: TutorSearchInput!) {
    tutors(input: $tutorSearchInput) {
      _id
      user {
        _id
        first
        last
      }
      ratings {
        category
        count
        total
      }
      statistics {
        lessonHistory {
          ...StatisticsFragment
        }
      }
      isAvailable
      privateLocationsStatus
    }
  }
  ${STATISTICS_FRAGMENT}
`;

const TUTOR_FRAGMENT = gql`
  fragment TutorFragment on Tutor {
    _id
    privateLocationsStatus
    bio
    courses {
      ...TutorCourseApprovalFragment
    }
    statistics {
      chatResponseTime {
        ...StatisticsFragment
      }
      lessonResponseTime {
        ...StatisticsFragment
      }
    }
  }
  ${TUTOR_COURSE_APPROVAL_FRAGMENT}
  ${STATISTICS_FRAGMENT}
`;

export const FETCH_TUTOR_QUERY = gql`
  query fetchTutor($tutor: ID!) {
    tutorByID(_tutor: $tutor) {
      ...TutorFragment
      user {
        ...TutorUserFragment
        paymentErrors {
          transactionType
          type
          code
          createdAt {
            timestamp
          }
        }
        notes {
          _id
          admin {
            user {
              first
              last
            }
          }
          body
          createdAt {
            timestamp
          }
        }
      }
      lessons {
        ...TutorLessonFragment
      }
      schedules {
        ...TutorScheduleFragment
      }
      paymentSet
      policies {
        studentRestriction {
          enabled
        }
        cancelation {
          enabled
          time
          penalty
        }
      }
      locations {
        _id
        name
        address
      }
      reviews {
        ...ReviewCustomFragment
      }
      reviewsGiven {
        ...ReviewCustomFragment
      }
    }
  }
  ${TUTOR_USER_FRAGMENT}
  ${TUTOR_FRAGMENT}
  ${TUTOR_LESSON_FRAGMENT}
  ${TUTOR_SCHEDULE_FRAGMENT}
  ${REVIEW_CUSTOM_FRAGMENT}
`;

export const FETCH_APPLICANT_QUERY = gql`
  query fetchApplicant($tutor: ID!) {
    tutorByID(_tutor: $tutor) {
      ...TutorFragment
      user {
        ...TutorUserFragment
        notes {
          _id
          admin {
            _id
            user {
              _id
              first
              last
            }
          }
          body
          createdAt {
            timestamp
          }
        }
      }
      applicant {
        ...ApplicantFragment
      }
    }
  }
  ${TUTOR_USER_FRAGMENT}
  ${TUTOR_FRAGMENT}
  ${APPLICANT_FRAGMENT}
`;

export const SET_REVIEW_VISIBILITY_MUTATION = gql`
  mutation setReviewVisibility($review: ID!, $visible: Boolean!) {
    reviewCustomizedSetVisibility(
      reviewCustomizedId: $review
      visible: $visible
    ) {
      _id
    }
  }
`;

export const ADD_NOTE_MUTATION = gql`
  mutation addNote($noteInput: NoteInput!) {
    addNote(input: $noteInput) {
      _id
    }
  }
`;

export const EDIT_NOTE_MUTATION = gql`
  mutation editNote($note: ID!, $body: String!) {
    editNote(_note: $note, body: $body) {
      _id
    }
  }
`;

export const REMOVE_NOTE_MUTATION = gql`
  mutation removeNote($note: ID!) {
    removeNote(_note: $note)
  }
`;

export const REMOVE_TUTOR_MUTATION = gql`
  mutation removeTutor($tutor: ID!) {
    removeTutor(_tutor: $tutor)
  }
`;



// WEBPACK FOOTER //
// ./src/modules/tutors/api.js